import { useCallback, useEffect, useState } from 'react';
import { HURRICANES_REPLAY_BASE_FRAME_INTERVAL_MS } from '../config/hurricanes.config';
import { getLastStormFrameIndex } from '../utils/hurricanes.utils';

// Owns the storm replay timeline: which track frame is shown, whether the
// replay is advancing, and how fast. The default position is the last frame
// so the map shows the complete track until the user starts a replay.
export default function useStormReplay({ frameCount = 0, resetKey = null } = {}) {
  const lastFrameIndex = getLastStormFrameIndex(frameCount);
  const [frameIndex, setFrameIndex] = useState(lastFrameIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1);

  // New storm (or dataset) selected: show its full track and stop playback.
  useEffect(() => {
    setFrameIndex(lastFrameIndex);
    setIsPlaying(false);
  }, [lastFrameIndex, resetKey]);

  // Advance one frame per tick while playing; stop at the end of the track.
  useEffect(() => {
    if (!isPlaying) return undefined;

    if (frameIndex >= lastFrameIndex) {
      setIsPlaying(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFrameIndex((currentIndex) => Math.min(currentIndex + 1, lastFrameIndex));
    }, HURRICANES_REPLAY_BASE_FRAME_INTERVAL_MS / replaySpeed);

    return () => window.clearTimeout(timeoutId);
  }, [isPlaying, frameIndex, lastFrameIndex, replaySpeed]);

  const togglePlayback = useCallback(() => {
    if (frameCount < 2) return;

    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    // Restart from the beginning when the replay already reached the end.
    if (frameIndex >= lastFrameIndex) {
      setFrameIndex(0);
    }
    setIsPlaying(true);
  }, [frameCount, frameIndex, isPlaying, lastFrameIndex]);

  const scrubToFrame = useCallback(
    (targetIndex) => {
      const parsedIndex = Number(targetIndex);
      if (!Number.isFinite(parsedIndex)) return;
      setFrameIndex(Math.min(Math.max(Math.round(parsedIndex), 0), lastFrameIndex));
    },
    [lastFrameIndex],
  );

  return {
    frameIndex,
    lastFrameIndex,
    isPlaying,
    replaySpeed,
    setReplaySpeed,
    togglePlayback,
    scrubToFrame,
  };
}
